import {
  Box,
  FormControl,
  FormControlLabel,
  type FormControlProps,
  FormLabel,
  RadioGroup,
  type SxProps,
  type Theme,
  useTheme,
} from "@mui/material";

import type React from "react";
import { type Control, Controller } from "react-hook-form";
import { convertStringToSentenceCase } from "utilz/helpers";
import { Radio } from "./Radio"; 

type RadioLabel = string | React.ReactNode;

interface RadioDataItem extends Record<string, unknown> {
  radioButtonProps?: Record<string, unknown>;
  component?: React.ReactNode;
}

const renderRadioLabel = (rawLabel: unknown): RadioLabel => {
  if (typeof rawLabel === "string") {
    // Do NOT String()-coerce here, that turns JSX into "[object Object]".
    return convertStringToSentenceCase(rawLabel);
  }
  // ReactNode (JSX, number, null, array of nodes) — render as-is.
  return rawLabel as React.ReactNode;
};

interface CustomRadioProps extends Omit<FormControlProps, "onChange"> {
  row?: boolean;
  label?: string;
  data: Array<RadioDataItem>;
  labelMappingKey: string;
  valueMappingKey: string;
  value?: string | number;
  onChange?: (value: string) => void;
  RadioGroupProps?: SxProps<Theme> | undefined;
  radioButtonStyle?: SxProps<Theme> | undefined;
  radioButtonContainerStyle?: SxProps<Theme> | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: RHF Control must accept any form shape
  control?: Control<any>;
  name?: string;
  labelVariant?: keyof Theme["typography"];
  labelColor?: string;
  customRadioButton?: (
    isChecked: boolean,
    item: RadioDataItem,
    {
      disabled,
      onChange,
    }: {
      disabled?: boolean;
      onChange?: (value: string) => void;
    },
  ) => React.ReactNode;
}
export const RadioButtonGroup = ({
  row = false,
  label,
  data,
  labelMappingKey,
  valueMappingKey,
  onChange,
  value,
  defaultValue,
  RadioGroupProps,
  radioButtonStyle,
  radioButtonContainerStyle,
  disabled = false,
  control,
  name,
  labelVariant = "body-small",
  labelColor,
  customRadioButton,
}: CustomRadioProps) => {
  const theme = useTheme();
  return control ? (
    <Controller
      name={name || ""}
      control={control}
      render={({ field: { ref, onChange, onBlur, value, name } }) => (
        <FormControl disabled={disabled}>
          <FormLabel>{label}</FormLabel>
          <RadioGroup
            row={row}
            ref={ref}
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={defaultValue ? defaultValue : ""}
            name="radio-buttons-group"
            onChange={(e) => onChange(e.target.value)}
            value={value}
            sx={{
              ...RadioGroupProps,
            }}
          >
            {data.map((item) =>
              customRadioButton ? (
                customRadioButton(value === item[valueMappingKey], item, {
                  disabled,
                  onChange: (value: string) => {
                    !disabled && onChange(value);
                  },
                })
              ) : (
                <Box key={String(item[valueMappingKey])}>
                  <FormControlLabel
                    sx={{
                      ...(theme.typography[
                        labelVariant as keyof typeof theme.typography
                      ] as object),
                      color: labelColor || theme.palette.lightBg.low,
                      ...radioButtonContainerStyle,
                      // marginBottom: "8px",
                    }}
                    value={String(item[valueMappingKey])}
                    control={
                      <Radio
                        sx={{ ...radioButtonStyle }}
                        {...item?.radioButtonProps}
                      />
                    }
                    label={renderRadioLabel(item[labelMappingKey])}
                  />
                  {value === item[valueMappingKey] && item.component && (
                    <Box sx={{ ml: 0, mb: 2 }}>{item.component}</Box>
                  )}
                </Box>
              ),
            )}
          </RadioGroup>
        </FormControl>
      )}
    />
  ) : (
    <FormControl disabled={disabled}>
      <FormLabel>{label}</FormLabel>
      <RadioGroup
        row={row}
        aria-labelledby="demo-radio-buttons-group-label"
        defaultValue={defaultValue ? defaultValue : ""}
        name="radio-buttons-group"
        onChange={(e) => {
          onChange?.(e.target.value);
        }}
        value={value}
        sx={{
          ...RadioGroupProps,
        }}
      >
        {data.map((item) =>
          customRadioButton ? (
            customRadioButton(value === item[valueMappingKey], item, {
              disabled,
              onChange: (value: string) => {
                !disabled && onChange?.(value);
              },
            })
          ) : (
            // <FormControlLabel
            //   key={item[valueMappingKey]}
            //   sx={{
            //     ...theme.typography["body-small"],
            //     color: theme.palette.lightBg.low,
            //   }}
            //   value={String(item[valueMappingKey])}
            //   control={
            //     <Radio
            //       sx={{ ...radioButtonStyle }}
            //       checkedIcon={<CustomRadioCheckedIcon />}
            //       icon={<CustomRadioIcon />}
            //       {...item?.radioButtonProps}
            //     />
            //   }
            // label={convertStringToSentenceCase(String(item[labelMappingKey]))}
            // />
            <Box key={String(item[valueMappingKey])}>
              <FormControlLabel
                sx={{
                  ...(theme.typography[
                    labelVariant as keyof typeof theme.typography
                  ] as object),
                  color: labelColor || theme.palette.lightBg.low,
                  ...radioButtonContainerStyle,
                  // marginBottom: "8px",
                }}
                value={String(item[valueMappingKey])}
                control={
                  <Radio
                    sx={{ ...radioButtonStyle }}
                    {...item?.radioButtonProps}
                  />
                }
                label={renderRadioLabel(item[labelMappingKey])}
              />
              {value === item[valueMappingKey] && item.component && (
                <Box sx={{ ml: 0, mb: 2 }}>{item.component}</Box>
              )}
            </Box>
          ),
        )}
      </RadioGroup>
    </FormControl>
  );
};
